import { get } from "lodash";
import { ChevronUp, Filter } from "lucide-react";
import PropTypes, { object } from "prop-types";
import React, {
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
  lazy,
  useEffect,
} from "react";
import { useDataProvider, useTranslate } from "react-admin";
import { useSharedTableComponent } from "../tableComponent/SharedTableComponent";
import usePageCasaDiVetro from "../../../utils/usePageCasaDiVetro";
import { useCasaDiVetroFilter } from "./ContextCasaDiVetro";
import { getEnv } from "../../../../config";
import Loader from "../../../../components/Loader";

const TableFiltersCasaDiVetro = lazy(() => import("./TableFiltersCasaDiVetro"));
const TableHeader = lazy(() => import("../TableHeader"));
const TablePaginator = lazy(() => import("../TablePaginator"));
const TableRow = lazy(() => import("../TableRow"));

const ROWS_PER_PAGE = 10;

const transformApiDataToTable = (apiData) => {
  if (!apiData) {
    return { headers: [], rows: [], filters: [] };
  }

  const headers = apiData.headers || [];
  const content = apiData.content || [];

  const rows = content.map((item) => {
    const row = {};
    headers.forEach((header) => {
      row[header.id] = {
        raw_value:
          item[header.id] !== null && item[header.id] !== undefined
            ? String(item[header.id])
            : "",
      };
    });
    return row;
  });

  const filters = headers.filter((h) => h._filterable);

  return { headers, rows, filters };
};

const TableRowsCasaDiVetro = ({ showAsResult = false, node }) => {
  const translate = useTranslate();
  const {
    filtersCDV,
    setFiltersCDV,
    pagination,
    setPage,
    setSortCdv,
    resetFiltersCDV,
  } = useCasaDiVetroFilter();
  const [expanded, setExpanded] = useState([]);
  const [currentPage, setCurrentPage] = useState(pagination.page + 1 || 1);
  const [filterValues, setFilterValues] = useState([filtersCDV]);
  const [showFilters, setShowFilters] = useState(
    Object.values(filtersCDV).find((e) => Boolean(e)),
  );
  const dirArrow = pagination.sort.split(",");
  const [sort, setSort] = useState(dirArrow[0]);
  const [direction, setDirection] = useState(dirArrow[1]);
  const dataProvider = useDataProvider();
  const [isLoading, setIsLoading] = useState(false);
  const [articleLoading, setArticleLoading] = useState(false);

  const {
    exportTable,
    modernTableStyles,
    ID_TABLE_CASA_DI_VETRO,
    responsiveTableWrap,
    tableBase,
  } = useSharedTableComponent();

  const listaDirezioneGenerale = get(
    usePageCasaDiVetro(getEnv("REACT_APP_CASA_DI_VETRO_GET_STRUTTURE"), {}),
    "listaDirezioneGenerale",
  );
  const listaTipologieAtto = usePageCasaDiVetro(
    getEnv("REACT_APP_CASA_DI_VETRO_GET_TIPOLOGIE_ATTO"),
    {},
  );

  const PATH_ARTICLE = `${getEnv("REACT_APP_CASA_DI_VETRO_GET_ARTICLE")}${
    node.reference_article
  }`;
  // const PATH_ARTICLE = `amministrazioneTrasparente/article${37}`

  const listaArticolo = usePageCasaDiVetro(PATH_ARTICLE, {
    ...filtersCDV,
    ...pagination,
  });

  const [isExporting, setIsExporting] = useState(false);
  const exportingRef = useRef(false);

  const { headers, rows, filters } = useMemo(() => {
    if (
      !listaArticolo ||
      (Array.isArray(listaArticolo) && listaArticolo.length === 0)
    )
      return { headers: [], rows: [] };
    return transformApiDataToTable(listaArticolo);
  }, [listaArticolo]);
  const totalRows = listaArticolo?.totalElements || 0;

  const handleExportError = useCallback((message) => {
    alert(message);
    setIsExporting(false);
    exportingRef.current = false;
  }, []);

  const columnsData = useMemo(
    () => ({
      columns: headers.filter((h) => h._preview),
      expandable: headers.filter((h) => !h._preview),
    }),
    [headers],
  );

  const paginationData = useMemo(
    () => ({
      pages: Math.ceil(totalRows / ROWS_PER_PAGE),
      hasMultiplePages: Math.ceil(totalRows / ROWS_PER_PAGE) > 1,
    }),
    [totalRows],
  );

  const handlePageChange = useCallback(
    (page) => {
      const cp = page - 1;
      setCurrentPage(page);
      setPage(cp);
    },
    [setPage],
  );

  const handleExport = useCallback(async () => {
    if (exportingRef.current || isExporting) return;
    exportingRef.current = true;
    setIsExporting(true);
    await exportTable(
      PATH_ARTICLE,
      {
        filter: filtersCDV,
        transformApiDataToTable,
      },
      handleExportError,
      dataProvider,
      ID_TABLE_CASA_DI_VETRO,
    );
    setIsExporting(false);
    exportingRef.current = false;
  }, [
    isExporting,
    exportTable,
    PATH_ARTICLE,
    filtersCDV,
    handleExportError,
    dataProvider,
    ID_TABLE_CASA_DI_VETRO,
  ]);

  const handleFiltersToggle = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  const normalizeFilter = useCallback((filter) => {
    return [...filter].sort((a, b) => {
      if ("*" in a) return -1;
      if ("*" in b) return 1;
      return 0;
    });
  }, []);

  const handleFiltersChange = useCallback(
    (newFilters) => {
      const normalizedValue = normalizeFilter(newFilters);
      setFilterValues(normalizedValue);

      //
      const mergedObject = Object.values(normalizedValue).reduce(
        (acc, curr) => {
          return { ...acc, ...curr };
        },
        {},
      );

      setFiltersCDV(mergedObject);
      //

      if (currentPage !== 1) {
        handlePageChange(1);
      }
    },
    [normalizeFilter, setFiltersCDV, currentPage, handlePageChange],
  );

  const handleFiltersClear = useCallback(() => {
    setFilterValues([]);
    resetFiltersCDV();
    if (currentPage !== 1) {
      handlePageChange(1);
    }
    localStorage.removeItem("CasaDiVetroParams");
  }, [currentPage, handlePageChange, resetFiltersCDV]);

  const toggleRow = useCallback((index, e) => {
    e.stopPropagation();
    e.preventDefault();
    setExpanded((prev) => {
      if (prev.includes(index)) {
        return prev.filter((item) => item !== index);
      } else {
        return [...prev, index];
      }
    });
  }, []);

  const handleSort = useCallback(
    (id, dir) => {
      const order = `${id},${dir}`;
      setSort(id);
      setDirection(dir);
      setSortCdv(order);
      if (currentPage !== 1) {
        handlePageChange(1);
      }
    },
    [currentPage, handlePageChange, setSortCdv],
  );

  const hasExpandableColumns = columnsData.expandable.length > 0;
  const hasNoRows = rows.length === 0;
  const shouldShowPagination = !showAsResult && paginationData.hasMultiplePages;

  useEffect(() => {
    const isLoadingDG = !listaDirezioneGenerale || listaDirezioneGenerale.error;
    const isLoadingTipo = !listaTipologieAtto || listaTipologieAtto.error;
    const isLoadingArt = !listaArticolo || listaArticolo.error;
    const isInitialLoading = isLoadingDG || isLoadingTipo;
    const isArticlesLoading = !isInitialLoading && isLoadingArt;

    if (isInitialLoading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
    if (!isInitialLoading && isArticlesLoading) {
      setArticleLoading(true);
    } else {
      setArticleLoading(false);
    }
  }, [listaDirezioneGenerale, listaTipologieAtto, listaArticolo]);

  if (isLoading) {
    return <Loader />;
  }

  if (showAsResult) {
    return null;
  }

  return (
    <div style={modernTableStyles.mainContainer}>
      {/* Filtri */}
      <div style={modernTableStyles.card}>
        <div style={modernTableStyles.cardHeader}>
          <button
            style={modernTableStyles.filtersToggle}
            onClick={handleFiltersToggle}
            className="hoverSortHeader"
          >
            <Filter size={20} />
            <span>{showFilters ? "Nascondi filtri" : "Mostra filtri"}</span>
            {showFilters ? (
              <ChevronUp className="is-collapsed" size={16} />
            ) : (
              <ChevronUp className="is-expanded" size={16} />
            )}
          </button>
        </div>

        {showFilters &&
          listaDirezioneGenerale &&
          listaTipologieAtto &&
          listaDirezioneGenerale.length > 0 &&
          listaTipologieAtto.length > 0 && (
            <div style={modernTableStyles.cardBody}>
              <Suspense fallback={<div>Loading filters...</div>}>
                <TableFiltersCasaDiVetro
                  searchable={true}
                  exportable={true}
                  filters={filters || []}
                  onExport={handleExport}
                  onChange={handleFiltersChange}
                  onClear={handleFiltersClear}
                  filterValues={filterValues}
                  listaDirezioneGenerale={
                    Array.isArray(listaDirezioneGenerale) &&
                    listaDirezioneGenerale.length > 0
                      ? listaDirezioneGenerale?.map((e) => ({
                          label: e?.struttura,
                          value: e?.struttura,
                        }))
                      : []
                  }
                  listaTipologieAtto={
                    Array.isArray(listaTipologieAtto) &&
                    listaTipologieAtto.length > 0
                      ? listaTipologieAtto?.map((e) => ({
                          label: e?.name,
                          value: e?.code,
                        }))
                      : []
                  }
                  staticFilter={true}
                  reference_article={node.reference_article}
                />
              </Suspense>
            </div>
          )}
      </div>

      {/* Tabella */}
      <div style={modernTableStyles.card}>
        {articleLoading ? (
          <Loader />
        ) : hasNoRows ? (
          <div style={modernTableStyles.noResultsContainer}>
            <div style={modernTableStyles.noResultsText}>
              Nessun risultato trovato
            </div>
            {Array.isArray(filters) && filters.length > 0 && (
              <div style={modernTableStyles.noResultsSubtext}>
                Prova a modificare i filtri di ricerca
              </div>
            )}
          </div>
        ) : (
          <div className="tableScrollX">
            <table style={{ ...modernTableStyles.table, ...tableBase }}>
              <thead style={modernTableStyles.tableHeader}>
                <tr>
                  {hasExpandableColumns && (
                    <th style={{ width: "50px", padding: "1rem 1rem" }}></th>
                  )}
                  {columnsData.columns.map((column) => (
                    <Suspense
                      key={get(column, "id")}
                      fallback={<th>Loading...</th>}
                    >
                      <TableHeader
                        column={column}
                        node={node}
                        onSort={handleSort}
                        sort={sort}
                        direction={direction}
                      />
                    </Suspense>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <Suspense
                    key={index}
                    fallback={
                      <tr>
                        <td>Loading...</td>
                      </tr>
                    }
                  >
                    <TableRow
                      columns={columnsData.columns}
                      expandable={columnsData.expandable}
                      expanded={expanded}
                      toggleRow={toggleRow}
                      index={index}
                      row={row}
                    />
                  </Suspense>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info paginazione e controlli */}
        {!showAsResult && !isLoading && (
          <>
            {!hasNoRows && (
              <div style={modernTableStyles.paginationInfo}>
                Pagina {currentPage} di {paginationData.pages} - Totale:{" "}
                {totalRows} risultati
              </div>
            )}

            {shouldShowPagination && (
              <Suspense fallback={<div>Loading pagination...</div>}>
                <TablePaginator
                  pages={paginationData.pages}
                  page={currentPage}
                  onChange={handlePageChange}
                />
              </Suspense>
            )}
          </>
        )}
      </div>
    </div>
  );
};

TableRowsCasaDiVetro.propTypes = {
  showAsResult: PropTypes.bool,
  listaDirezioneGenerale: PropTypes.array,
  listaTipologieAtto: PropTypes.array,
  node: PropTypes.object,
};

export default React.memo(TableRowsCasaDiVetro);
